import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import {addNewPage} from '@Components/poster-editor/library/poster-editor-page-library';
import styles from './add-new-page-button.module.scss';
import {useGetNumberOfPages} from '@Hooks/poster-editor/useGetNumberOfPages';
import {Tooltip} from '@Components/tooltip';
import {Text, TextSize} from '@Components/text';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updateTriggerOpenTemplatesPopup, getIsPosterInitialized, updateTriggerOpenMyDesignsPopup, selectIsTimelineModeOn} from '@Components/poster-editor/poster-editor-reducer';
import {useShowContextualSkeletons} from '@Hooks/poster-editor/useShowContextualSkeletons';
import {AddPageGA4EventParams, GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {useIsSinglePageLayout} from '@Hooks/poster-editor/useIsSinglePageLayout';
import {MAX_NUMBER_OF_PAGES} from '@PosterWhiteboard/poster/poster.class';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {ControlledList} from '@Components/controlled-list';
import {ClickableDiv} from '@Components/clickable-div';
import {AddPageOptions, PageApplyMode} from '@Components/poster-editor/components/add-new-page-button/add-new-page-button.types';
import {getAddPageOptionItems} from '@Components/poster-editor/components/add-new-page-button/add-new-page-button.library';

export function AddNewPageButton(): ReactElement | null {
  const dispatch = useAppDispatch();
  const numPages = useGetNumberOfPages();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const isMultiPageEnabled = window.PMW.isMultipageAvailable;
  const isSinglePageLayout = useIsSinglePageLayout();
  const isTimelineModeOn = useAppSelector(selectIsTimelineModeOn);
  const isVersionHistoryModeActive = useAppSelector((state) => {
    return state.posterEditor.isVersionHistoryModeActive;
  });
  const isPosterLoading = useShowContextualSkeletons();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const isPosterInitialized = useAppSelector(getIsPosterInitialized);

  if (isSinglePageLayout || isTimelineModeOn || isVersionHistoryModeActive || !isMultiPageEnabled || isPosterLoading) {
    return null;
  }

  const isDisabled = numPages >= MAX_NUMBER_OF_PAGES || !isPosterInitialized;

  function onAddBlankPage(): void {
    trackPosterBuilderGA4Events(GA4EventName.ADD_PAGE, {
      [GA4EventParamName.TYPE]: AddPageGA4EventParams.PAGE_BOTTOM_BUTTON,
    });
    addNewPage().catch((e: unknown) => {
      console.error(e);
    });
  }

  const onMouseEnter = (): void => {
    if (numPages < MAX_NUMBER_OF_PAGES) {
      return;
    }

    setShowTooltip(true);
  };
  const onMouseLeave = (): void => {
    if (showTooltip) {
      setShowTooltip(false);
    }
  };

  const getSelector = (): ReactElement => {
    return (
      <div
        className={`${styles.selector} ${isDropdownOpen ? styles.active : ''} ${isDisabled ? styles.disabled : ''} hover-transition-all flex-center _full-height _cursor-pointer spacing-p-2`}
      >
        <Icon icon={isDropdownOpen ? 'icon-caret-up' : 'icon-caret-down'} size={IconSize.SIZE_ICON_16} />
      </div>
    );
  };

  const items = getAddPageOptionItems([AddPageOptions.DESIGN, AddPageOptions.TEMPLATE, AddPageOptions.NEW_PAGE]);

  const onClickListItems = (id: AddPageOptions): void => {
    switch (id) {
      case AddPageOptions.TEMPLATE:
        dispatch(updateTriggerOpenTemplatesPopup(PageApplyMode.ADD));
        break;
      case AddPageOptions.DESIGN:
        dispatch(updateTriggerOpenMyDesignsPopup(PageApplyMode.ADD));
        break;
      case AddPageOptions.NEW_PAGE:
        onAddBlankPage();
        break;
    }
  };

  const getPopup = (): ReactElement => {
    return <ControlledList onClick={onClickListItems} items={items} />;
  };

  const getCaret = (): ReactElement => {
    if (isDisabled) {
      return getSelector();
    }
    return (
      <Dropdown
        popUpHasCustomWidth
        alignment={ALIGNMENT_TYPE.TOP_START}
        popupSpacingClass="spacing-p-2"
        popupClassName={styles.popup}
        popupOffset={8}
        popup={getPopup()}
        selector={getSelector()}
        onOpen={(): void => {
          setIsDropdownOpen(true);
        }}
        onClose={(): void => {
          setIsDropdownOpen(false);
        }}
      />
    );
  };

  return (
    <div className={`${styles.container} _position-relative flex-center`} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} ref={containerRef}>
      {showTooltip ? (
        <Tooltip className={styles.tooltip} id="max-limit-tooltip" text={<Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_max_page_limit_message')} />} />
      ) : null}
      <div className={`${styles.buttonWrapper} _position-relative border-s-standard shadow-floating-sm flex-h-row radius-8`}>
        <ClickableDiv
          onClick={(e): void => {
            (e.currentTarget as HTMLElement).blur();
            if (isDisabled) {
              return;
            }
            onAddBlankPage();
          }}
          className={`_full-width flex-row-align-center spacing-p-l-3 spacing-p-r-3 _cursor-pointer hover-transition-all ${styles.buttonContainer} ${isDisabled ? styles.disabled : ''}`}
        >
          <Icon icon="icon-new-page" size={IconSize.SIZE_ICON_16} className={styles.icon} />
          <Text className="_fit-height" val={window.i18next.t('pmwjs_add_page')} size={TextSize.SM} bold />
        </ClickableDiv>
        {getCaret()}
      </div>
    </div>
  );
}
